// src/features/inventory/pages/ItemDetailPage/tabs/Suppliers/index.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useInventorySupplierPermissions } from '../../../../../auth/hooks/usePermissions';
import { useItemSuppliers } from '../../../../hooks/useItemSuppliers';
import LinkSupplierDialog from './LinkSupplierDialog';

export default function Suppliers({ itemId }) {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const { canUpdate } = useInventorySupplierPermissions();
  const { suppliers, loading, error, clearError, fetchSuppliersForItem } = useItemSuppliers();
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchSuppliersForItem(itemId);
  }, [fetchSuppliersForItem, itemId]);

  const linkedSupplierIds = suppliers.map(s => s.supplier_id);

  const rows = suppliers.map(s => {
    const link = s.supplier_items?.find(si => si.item_id === itemId);
    return {
      id: s.supplier_id,
      name: s.name,
      code: s.code,
      active: s.active,
      contact_email: s.contact_email ?? null,
      supplier_item_number: link?.supplier_item_number ?? null,
      barcode: link?.barcode ?? null,
    };
  });

  const columns = [
    {
      field: 'name',
      headerName: t('inventory:items.tabs.suppliers.columns.supplier'),
      flex: 1.5,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight="600">{row.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.code}</Typography>
        </Box>
      ),
    },
    {
      field: 'supplier_item_number',
      headerName: t('inventory:items.tabs.suppliers.supplier_sku'),
      flex: 1,
      renderCell: ({ value }) => value
        ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{value}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    {
      field: 'barcode',
      headerName: t('inventory:items.tabs.suppliers.barcode'),
      flex: 1,
      renderCell: ({ value }) => value
        ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{value}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    {
      field: 'contact_email',
      headerName: t('inventory:items.tabs.suppliers.columns.email'),
      flex: 1.5,
      renderCell: ({ value }) => value
        ? <Typography variant="body2">{value}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    {
      field: 'active',
      headerName: t('common:labels.status'),
      flex: 0.7,
      renderCell: ({ value }) => (
        <Chip
          label={value ? t('common:status.active') : t('common:status.inactive')}
          color={value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
  ];

  return (
    <Box display="flex" flex="1" minHeight="0" flexDirection="column">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>{error}</Alert>
      )}

      {canUpdate && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            {t('inventory:items.tabs.suppliers.dialog.open_button')}
          </Button>
        </Box>
      )}

      {suppliers.length === 0 && !loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('inventory:items.tabs.suppliers.empty')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            onRowClick={(params) => navigate(`/inventory/suppliers/${params.row.id}`)}
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={{
              flex: 1,
              border: 'none',
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-cell': { alignItems: 'center' },
            }}
          />
        </Box>
      )}

      <LinkSupplierDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => fetchSuppliersForItem(itemId)}
        itemId={itemId}
        linkedSupplierIds={linkedSupplierIds}
      />
    </Box>
  );
}
